import React, { Fragment, memo, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import SurveyReport from './SurveyReport';
import { getLayerConfig } from '../Prescription/PrescriptionUtils';
import { getImagesForDate } from '../../helpers/StateInterpreters';
import WebAPIUtils from '../../WebAPIUtils';
import { useFarm } from '../../context/AccountContext';
import { useLangFile } from 'js/context/LanguageContext';
import { Print } from '@mui/icons-material';
import useFirebaseAnalytics, { FIREBASE_EVENTS } from '../../hooks/useFirebaseAnalytics';
import { Fab, Tooltip } from '@mui/material';
import { isSatelliteLayer, isSurveyLayer, isYieldLayer } from '../Prescription/PrescriptionJob';

const mapStateToProps = (store) => {
  return {
    date: store.field.date?.date,
    images: store.field.images,
    imageType: store.field.imageType,
    selectedField: store.field.selectedField,
    viewMode: store.control.viewMode,
    variationsEnabled: store.survey.variationsEnabled,
    classificationsEnabled: store.survey.classificationsEnabled,
    surveys: store.survey.surveys,
    selectedLayer: store.survey.selectedLayer,
    selectedFieldDates: store.field.selectedFieldDates,
    selectedSurveyClassificationValues: store.survey.selectedSurveyClassificationValues,
    yieldData: store.yield.yieldData,
    selectedSurvey: store.survey.selectedSurvey,
  };
};

const SurveyReportContainer = (props: SurveyReportContainer.propTypes) => {
  const analytics = useFirebaseAnalytics();
  const LangFile = useLangFile();
  const farm = useFarm();
  const layer = isSatelliteLayer(props.selectedLayer) ? props.imageType : props.selectedLayer;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [imageURL, setImageURL] = useState(null);
  const [referenceValues, setReferenceValues] = useState({});
  const [values, setValues] = useState(null);
  const [yieldEntry, setYieldEntry] = useState(null);

  const [selectedFieldHasImage, setSelectedFieldHasImage] = useState(false);
  const [selectedFieldHasSurvey, setSelectedFieldHasSurvey] = useState(false);
  const [selectedFieldHasYield, setSelectedFieldHasYield] = useState(false);

  const handleDialogOpen = (open) =>
    useCallback(() => {
      if (open) {
        analytics.logEvent(FIREBASE_EVENTS.ANALYSIS_PRINT_OPEN);
      } else {
        analytics.logEvent(FIREBASE_EVENTS.ANALYSIS_PRINT_CLOSE);
      }

      setDialogOpen(open);
    }, []);

  useEffect(() => {
    if (!dialogOpen) {
      return;
    }
    if (props.selectedField) {
      WebAPIUtils.getLayerValues(
        farm.farmId,
        props.selectedField.fieldId,
        props.date,
        layer,
        props.selectedSurvey
      ).then((val) => {
        setReferenceValues((current) => ({ ...current, [layer]: val }));
      });
    }
  }, [props.selectedSurvey, props.date, props.selectedField, layer, dialogOpen]);

  useEffect(() => {
    if (!dialogOpen) {
      return;
    }
    setSelectedFieldHasImage(
      Boolean(props.selectedFieldDates && props.date && props.selectedFieldDates[props.date])
    );
  }, [props.selectedFieldDates, props.date, dialogOpen]);

  useEffect(() => {
    if (!dialogOpen) {
      return;
    }
    setSelectedFieldHasSurvey(
      Boolean(
        props.surveys &&
          props.selectedField &&
          props.surveys.find((s) => s.fieldId === props.selectedField.fieldId)
      )
    );
  }, [props.selectedField, props.surveys, dialogOpen]);

  useEffect(() => {
    if (!dialogOpen) {
      return;
    }
    setSelectedFieldHasYield(
      Boolean(
        props.selectedField &&
          props.yieldData[props.selectedField.fieldId] &&
          props.yieldData[props.selectedField.fieldId].find((data) => data.date === props.date)
      )
    );
  }, [props.selectedField, props.yieldData, dialogOpen]);

  useEffect(() => {
    if (!dialogOpen) {
      return;
    }

    if (referenceValues && props.selectedLayer) {
      setValues(referenceValues[props.selectedLayer]);
    }
  }, [referenceValues, dialogOpen]);

  useEffect(() => {
    if (!dialogOpen) {
      return;
    }

    // Handles initialization and change of satellite imagery.
    if (isSatelliteLayer(layer)) {
      const images = getImagesForDate(props.date, props.images, props.imageType);
      const imageMap = new Map(images.map((image) => [image.fieldId, image]));
      const imagePack = imageMap.get(props.selectedField.fieldId);
      if (imagePack) {
        const url = imagePack[props.imageType];
        setImageURL(url);
      }
    }
    // Handles initialization and change of survey based on the available surveys and the selected field.
    else if (isSurveyLayer(layer)) {
      const config = getLayerConfig(layer);
      const images = Object.assign(props.selectedSurvey.images, props.selectedSurvey.newImages);
      const url = config.getImageUrl(
        images,
        props.classificationsEnabled,
        props.variationsEnabled,
        props.viewMode
      );
      setImageURL(url);
    }
    // Handles yield
    else if (isYieldLayer(layer)) {
      const yieldData = props.yieldData[props.selectedField.fieldId].find(
        (data) => data.date === props.date
      );
      if (yieldData) {
        const url = yieldData.url;
        setImageURL(url);
        setYieldEntry(yieldData);
      }
    } else {
      setImageURL(null);
      setSurvey(null);
    }
  }, [
    props.date,
    props.images,
    props.imageType,
    props.showSatelliteImagery,
    props.selectedField,
    props.showSoilSurveys,
    props.surveys,
    props.selectedField,
    props.selectedLayer,
    props.classificationsEnabled,
    props.variationsEnabled,
    props.viewMode,
    dialogOpen,
    props.selectedSurvey,
  ]);

  return (
    <Fragment>
      <Tooltip title={LangFile.SurveyReport.print}>
        <Fab size="small" onClick={handleDialogOpen(true)}>
          <Print />
        </Fab>
      </Tooltip>

      <SurveyReport
        open={dialogOpen}
        onClose={handleDialogOpen(false)}
        selectedField={props.selectedField}
        imageURL={imageURL}
        viewMode={props.viewMode}
        imageType={props.imageType}
        imageDate={props.date}
        selectedLayer={props.selectedLayer}
        variationsEnabled={props.variationsEnabled}
        selectedFieldHasImage={selectedFieldHasImage}
        selectedFieldHasSurvey={selectedFieldHasSurvey}
        selectedFieldHasYield={selectedFieldHasYield}
        classificationsEnabled={props.classificationsEnabled}
        values={values}
        classificationValues={props.selectedSurveyClassificationValues}
        yieldEntry={yieldEntry}
      />
    </Fragment>
  );
};

SurveyReportContainer.propTypes = {
  surveys: PropTypes.array,
  selectedLayer: PropTypes.string,
  date: PropTypes.string,
  images: PropTypes.object,
  imageType: PropTypes.string,
  viewMode: PropTypes.string,
  selectedField: PropTypes.object,
  variationsEnabled: PropTypes.bool,
  classificationsEnabled: PropTypes.bool,
  selectedFieldDates: PropTypes.array,
  selectedSurveyClassificationValues: PropTypes.object,
  yieldData: PropTypes.object,
  selectedSurvey: PropTypes.object,
};

export default memo(connect(mapStateToProps)(SurveyReportContainer));
